<template>
  <h1 class="text-center text-plum mb-lgp">
    Change Plan
  </h1>
  
  <div
    v-if="!eligibleForChange && ineligibilityReasonText"
    class="mb-lgp">
    <div class="alert alert-warning text-center">
      <p v-html="ineligibilityReasonText"/>
    </div>
  </div>

  <div class="change-plan-wrapper">
    <div
      class="d-flex gap-md"
      :class="{ 'flex-column w-100 align-items-center': shouldUseTabletLayout }">
      <div
        v-for="plan in availableMonthlyPlans"
        :key="plan.id"
        :class="`order-lg-${getPlanOrder(plan.id)}`"
        class="plan-card-wrapper flex-grow-1">
        <MembershipPlanCard
          :plan="plan"
          :force-selected="selectedHeroPlan === plan.id"
          :current-plan="currentPlan?.id"
          :starting-at-price="startingAtPrices[plan.nItems]"
          @selected="onPlanSelected(plan.id)"
          @option-selected="onPaymentOptionSelected"/>
      </div>
    </div>
    <CalloutSection
      v-if="membershipPlans && !shouldUseTabletLayout && selectedHeroPlan"
      :position="calloutPosition">
      <template #default>
        <FadeTransition>
          <div class="d-flex flex-column align-items-center">
            <h5>Choose your payment option</h5>
            <div class="payment-options-wrapper d-flex flex-wrap gap-md">
              <PlanCardPaymentOption
                v-for="paymentOption in visiblePaymentOptions"
                :key="paymentOption.id"
                :plan="paymentOption"
                @selected="onPaymentOptionSelected(paymentOption.id)"/>
            </div>
          </div>
        </FadeTransition>
      </template>
    </CalloutSection>
  </div>
  <BaseStickyFooter
    :is-visible="selectedPlanId && selectedPlanId !== currentPlan?.id"
    :teleport-to="'body'">
    <template #content>
      <div class="d-flex justify-content-center">
        <BaseButton
          :text="buttonText"
          :disabled="!eligibleForChange" 
          @click="onChangePlan"/>
      </div>
    </template>
  </BaseStickyFooter>
</template>

<script setup>
import { onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSubscriptionStore } from '@/stores/subscribe.js'
import { usePlanSelection } from '@shared/composables/planSelection.js'
import { usePlans } from '@shared/composables/plans.js'
import MembershipPlanCard from '@/components/account/membership/new/MembershipPlanCard.vue'
import CalloutSection from '@shared/components/ADORN/CalloutSection.vue'
import FadeTransition from '@shared/components/ADORN/transitions/FadeTransition.vue'
import PlanCardPaymentOption from '@shared/components/ADORN/planCard/PlanCardPaymentOption.vue'
import BaseStickyFooter from '@shared/components/ADORN/BaseStickyFooter.vue'
import BaseButton from '@shared/components/ADORN/BaseButton.vue'

const subscriptionStore = useSubscriptionStore()
const { initializeMembershipPlans } = usePlans()
const route = useRoute()
const router = useRouter()

const {
  selectedHeroPlan,
  membershipPlans,
  availableMonthlyPlans,
  selectedPlan,
  currentPlan,
  visiblePaymentOptions,
  calloutPosition,
  getPlanOrder,
  onPlanSelected,
  onPaymentOptionSelected,
  startingAtPrices,
  selectedPlanId,
  getCustomDisplayName,
  eligibleForChange,
  ineligibilityReasonText,
  shouldUseTabletLayout,
} = usePlanSelection()

const buttonText = computed(() => 
  selectedPlan.value ? getCustomDisplayName(selectedPlan.value) ? `Change to ${getCustomDisplayName(selectedPlan.value)}` : 'Change Plan' : 'Change Plan'
)
const onChangePlan = () => {
  const planId = selectedPlanId.value || route.query.plan
  if (planId) {
    router.push({
      name: 'confirm-plan-change',
      query: { planId: planId, source: 'change-plan' }
    })
  }
}

onMounted(async () => {
  selectedHeroPlan.value = ''
  selectedPlanId.value = null
  
  if (route.query.plan) {
    const { restQuery } = route.query
    router.replace({ query: restQuery })
  }
  
  await initializeMembershipPlans()
  availableMonthlyPlans.value.forEach(plan => {
    subscriptionStore.getQuote({ planId: plan.id })
  })

  if (currentPlan.value) {
    const currentPlanInMembershipPlans = membershipPlans.value?.find(plan => plan.id === currentPlan.value.id)
    
    const availableHeroPlan = availableMonthlyPlans.value.find(plan => plan.nItems === currentPlan.value.nItems)
    
    if (currentPlanInMembershipPlans && availableHeroPlan) {
      selectedHeroPlan.value = availableHeroPlan.id
      onPaymentOptionSelected(currentPlan.value.id)
    }
  }
})

</script>

<style scoped lang="scss">
.change-plan-wrapper {
  max-width: 1150px;
  margin: 0 auto;
  padding-bottom: 80px; // Add padding to prevent content from being hidden behind sticky footer
}

.plan-card-wrapper {
  width: 100%;
  max-width: 375px;
}

.payment-options-wrapper {
  @media screen and (max-width: 1314px) {
    max-width: 500px;
  }
}

// Ensure proper scrolling on mobile
@media screen and (max-width: 768px) {
  .change-plan-wrapper {
    padding-bottom: 100px; // Extra padding for mobile sticky footer
  }
}
</style>